import React, { useState, useEffect, useContext } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ConfigContext } from "../App";
import { formatPrice } from "../functions/utils";
import config from "../configuration.json";

/**
 * Admin orders management page component.
 * Provides a checklist interface for managing orders with filtering, status updates, and notes.
 */
function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, completed
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const configuration = useContext(ConfigContext);

  useEffect(() => {
    // Check if user is authenticated
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/admin/login");
      }
    });

    // Listen to orders collection
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsubscribeOrders = onSnapshot(q, (querySnapshot) => {
      const ordersData = [];
      querySnapshot.forEach((doc) => {
        ordersData.push({ id: doc.id, ...doc.data() });
      });
      setOrders(ordersData);
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeOrders();
    };
  }, [navigate]);

  useEffect(() => {
    // Filter orders based on status and search term
    let filtered = orders;

    if (filter !== "all") {
      filtered = filtered.filter((order) => order.status === filter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.customer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort: pending orders first, then completed
    filtered.sort((a, b) => {
      if (a.status === "pending" && b.status === "completed") return -1;
      if (a.status === "completed" && b.status === "pending") return 1;
      return 0;
    });

    setFilteredOrders(filtered);
  }, [orders, filter, searchTerm]);

  /**
   * Handles order status toggle.
   * @param {string} orderId - The order ID.
   * @param {string} currentStatus - The current status.
   */
  const handleStatusToggle = async (orderId, currentStatus) => {
    const newStatus = currentStatus === "pending" ? "completed" : "pending";
    const updateData = {
      status: newStatus,
      completedAt: newStatus === "completed" ? serverTimestamp() : null,
    };

    try {
      await updateDoc(doc(db, "orders", orderId), updateData);
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  /**
   * Handles notes update for an order.
   * @param {string} orderId - The order ID.
   * @param {string} notes - The notes content.
   */
  const handleNotesUpdate = async (orderId, notes) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { notes });
    } catch (error) {
      console.error("Error updating order notes:", error);
    }
  };

  /**
   * Handles admin logout.
   */
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  /**
   * Formats a Firestore timestamp to a readable date string.
   * @param {Object} timestamp - Firestore timestamp.
   * @returns {string} Formatted date string.
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return (
      date.toLocaleDateString("bg-BG") +
      " " +
      date.toLocaleTimeString("bg-BG", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Поръчки - {configuration.title}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Поръчки</h1>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Изход
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Търси по име, мейл или ID на поръчка..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="filter"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Всички поръчки</option>
                  <option value="pending">Очакващи</option>
                  <option value="completed">Изпълнени</option>
                </select>
              </div>
            </div>
          </div>

          {/* Orders List */}
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="bg-white shadow rounded-lg p-6 text-center">
                <p className="text-gray-500">Няма поръчки, отговарящи на критериите.</p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className={`bg-white shadow rounded-lg p-6 ${
                    order.status === "completed" ? "opacity-60 bg-gray-50" : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Checkbox */}
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={order.status === "completed"}
                          onChange={() => handleStatusToggle(order.id, order.status)}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>

                      {/* Order Details */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">Поръчка #{order.orderNumber ? order.orderNumber.toString().padStart(7, '0') : order.id.slice(-8)}</h3>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              order.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {order.status === "completed" ? "Изпълнена" : "Очакваща"}
                          </span>
                        </div>

                        <div className="flex flex-col gap-2 mb-4">
                          <div className="flex gap-2">
                            <h4 className="text-sm font-medium text-gray-500">
                              Клиент:{" "}
                              <span className="font-bold text-gray-900">
                                {order.customer.firstName} {order.customer.lastName}
                              </span>
                            </h4>
                            <p className="text-sm text-gray-600">| {order.customer.email}</p>
                            <p className="text-sm text-gray-600">| {order.customer.phone}</p>
                          </div>

                          <div className="flex flex-col gap-1">
                            <div className="flex gap-2 flex-wrap">
                              <h4 className="text-sm font-medium text-gray-500">
                                Доставка:{" "}
                                <span className="font-bold text-gray-900">
                                  {order.shipping.carrierName || order.shipping.delivery}
                                  {order.shipping.destinationType
                                    ? ` · ${
                                        order.shipping.destinationType === "home"
                                          ? "Адрес"
                                          : order.shipping.destinationType === "office"
                                            ? "Офис"
                                            : order.shipping.destinationType === "box"
                                              ? "Автомат"
                                              : order.shipping.destinationType
                                      }`
                                    : ""}
                                </span>
                              </h4>
                              <p className="text-sm text-gray-600">
                                | {order.shipping.city}, {order.shipping.address}, {order.shipping.postalCode}
                              </p>
                            </div>
                            {order.shipping.office?.name && (
                              <p className="text-sm text-gray-600">
                                Офис/автомат: {order.shipping.office.name}
                                {order.shipping.office.id != null ? ` (#${order.shipping.office.id})` : ""}
                              </p>
                            )}
                          </div>
                          <hr className="my-2" />
                          <div>
                            <h4 className="text-sm font-medium text-gray-500">Обобщение:</h4>
                            <p className="text-sm text-gray-600">Дата на поръчка: {formatDate(order.createdAt)}</p>
                            <p className="text-sm text-gray-900">
                              Обща сума: {formatPrice(order.total, "bg", config)}
                            </p>
                            <p className="text-sm text-gray-600">Брой поръчани продукти: {order.items.length}</p>
                          </div>
                        </div>
                        <hr className="my-2" />

                        {/* Items */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-500 mb-2">Продукти:</h4>
                          <div className="space-y-1">
                            {order.items.map((item, index) => (
                              <div key={index} className="text-sm text-gray-600">
                                {item.title.bg || item.title} x{item.quantity} -{" "}
                                {formatPrice(item.price, "bg", config)}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Бележки:</label>
                          <textarea
                            value={order.notes || ""}
                            onChange={(e) => handleNotesUpdate(order.id, e.target.value)}
                            placeholder="Добави бележка към тази поръчка... (за вътрешно ползване)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminOrders;
